import React, { useState } from 'react';
import { FiX, FiCheck, FiShoppingCart } from 'react-icons/fi';
import { stripeAPI } from '../utils/api';
import { trackAddOnSelect, trackCheckoutClick, trackModalClose } from '../utils/analytics';
import './CheckoutModal.css';

const CheckoutModal = ({ template, isOpen, onClose }) => {
  const [addFullSetup, setAddFullSetup] = useState(false);
  const [addHosting, setAddHosting] = useState(false);
  const [addAnalytics, setAddAnalytics] = useState(false);
  const [loading, setLoading] = useState(false);

  if (!isOpen || !template) return null;

  const templatePrice = template.price || 299;
  const fullSetupPrice = 499;
  const hostingPrice = 100;
  const analyticsPrice = 299;

  const oneTimeTotal = templatePrice + (addFullSetup ? fullSetupPrice : 0) + (addAnalytics ? analyticsPrice : 0);
  const monthlyTotal = addHosting ? hostingPrice : 0;

  const handleFullSetupChange = (checked) => {
    setAddFullSetup(checked);
    trackAddOnSelect('Full Setup', checked, template.title);
  };

  const handleHostingChange = (checked) => {
    setAddHosting(checked);
    trackAddOnSelect('Hosting', checked, template.title);
  };

  const handleAnalyticsChange = (checked) => {
    setAddAnalytics(checked);
    trackAddOnSelect('Analytics', checked, template.title);
  };

  const handleClose = () => {
    trackModalClose(template.title);
    onClose();
  };

  const handleCheckout = async () => {
    setLoading(true);

    // Track checkout click with add-ons
    const selectedAddOns = [];
    if (addFullSetup) selectedAddOns.push('Full Setup');
    if (addHosting) selectedAddOns.push('Hosting');
    if (addAnalytics) selectedAddOns.push('Analytics');
    trackCheckoutClick(template.title, oneTimeTotal, selectedAddOns);

    try {
      const { data } = await stripeAPI.createCheckoutSession(template.slug, {
        addFullSetup,
        addHosting,
        addAnalytics
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Error starting checkout. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="sfm-checkout-modal__overlay" onClick={handleClose}>
      <div className="sfm-checkout-modal" onClick={(e) => e.stopPropagation()}>
        <button className="sfm-checkout-modal__close" onClick={handleClose}>
          <FiX />
        </button>

        <div className="sfm-checkout-modal__header">
          <h2>Complete Your Order</h2>
          <p>Customize your {template.title} purchase</p>
        </div>

        <div className="sfm-checkout-modal__content">
          {/* Base Template */}
          <div className="sfm-checkout-modal__item sfm-checkout-modal__item--base">
            <div className="sfm-checkout-modal__item-info">
              <div className="sfm-checkout-modal__item-check">
                <FiCheck />
              </div>
              <div>
                <h3>{template.title} Template</h3>
                <p>HTML/CSS/JS files, images, documentation</p>
              </div>
            </div>
            <div className="sfm-checkout-modal__item-price">
              ${templatePrice}
            </div>
          </div>

          {/* Full Setup Add-on */}
          <label className={`sfm-checkout-modal__item sfm-checkout-modal__item--addon ${addFullSetup ? 'selected' : ''}`}>
            <div className="sfm-checkout-modal__item-info">
              <input
                type="checkbox"
                checked={addFullSetup}
                onChange={(e) => handleFullSetupChange(e.target.checked)}
              />
              <div>
                <h3>Full Setup Service</h3>
                <p>Custom branding, content updates, domain setup, SSL certificate</p>
              </div>
            </div>
            <div className="sfm-checkout-modal__item-price">
              +${fullSetupPrice}
            </div>
          </label>

          {/* Hosting Add-on */}
          <label className={`sfm-checkout-modal__item sfm-checkout-modal__item--addon ${addHosting ? 'selected' : ''}`}>
            <div className="sfm-checkout-modal__item-info">
              <input
                type="checkbox"
                checked={addHosting}
                onChange={(e) => handleHostingChange(e.target.checked)}
              />
              <div>
                <h3>Hosting & Maintenance</h3>
                <p>Fast cloud hosting, SSL, monthly backups, content updates, priority support</p>
              </div>
            </div>
            <div className="sfm-checkout-modal__item-price">
              +${hostingPrice}<span>/mo</span>
            </div>
          </label>

          {/* Analytics Setup Add-on */}
          <label className={`sfm-checkout-modal__item sfm-checkout-modal__item--addon ${addAnalytics ? 'selected' : ''}`}>
            <div className="sfm-checkout-modal__item-info">
              <input
                type="checkbox"
                checked={addAnalytics}
                onChange={(e) => handleAnalyticsChange(e.target.checked)}
              />
              <div>
                <h3>Analytics Setup</h3>
                <p>Google Analytics 4 + Search Console setup, conversion tracking, ROI reporting dashboard</p>
              </div>
            </div>
            <div className="sfm-checkout-modal__item-price">
              +${analyticsPrice}
            </div>
          </label>
        </div>

        <div className="sfm-checkout-modal__footer">
          <div className="sfm-checkout-modal__total">
            <div className="sfm-checkout-modal__total-row">
              <span>One-time total:</span>
              <span className="sfm-checkout-modal__total-value">${oneTimeTotal}</span>
            </div>
            {monthlyTotal > 0 && (
              <div className="sfm-checkout-modal__total-row sfm-checkout-modal__total-row--monthly">
                <span>Monthly:</span>
                <span className="sfm-checkout-modal__total-value">${monthlyTotal}/mo</span>
              </div>
            )}
          </div>
          <button
            className="sfm-checkout-modal__btn"
            onClick={handleCheckout}
            disabled={loading}
          >
            <FiShoppingCart />
            {loading ? 'Processing...' : 'Proceed to Checkout'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutModal;
